import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { PendingRestaurantDto, PendingMenuItemDto } from '../../types';
import './Admin.css';

const AdminPanel: React.FC = () => {
  const [restaurants, setRestaurants] = useState<PendingRestaurantDto[]>([]);
  const [menuItems, setMenuItems] = useState<PendingMenuItemDto[]>([]);
  const [tab, setTab] = useState<'restaurants' | 'menuItems'>('restaurants');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPending = async () => {
    setError('');
    try {
      const [restRes, menuRes] = await Promise.all([
        api.get('/admin/pending-restaurants'),
        api.get('/admin/pending-menu-items'),
      ]);
      setRestaurants(restRes.data);
      setMenuItems(menuRes.data);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('You do not have admin access.');
      } else {
        setError('Failed to load pending items. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const handleRestaurant = async (id: number, action: 'approve' | 'reject') => {
    try {
      await api.put(`/admin/restaurants/${id}/${action}`);
      fetchPending();
    } catch (err: any) {
      alert(err.response?.data?.message || `Failed to ${action} restaurant`);
    }
  };

  const handleMenuItem = async (id: number, action: 'approve' | 'reject') => {
    try {
      await api.put(`/admin/menu-items/${id}/${action}`);
      fetchPending();
    } catch (err: any) {
      alert(err.response?.data?.message || `Failed to ${action} menu item`);
    }
  };

  if (loading) return <p className="loading">Loading...</p>;
  if (error) return <div className="page-container"><p className="error-text" style={{ color: '#dc3545', textAlign: 'center', marginTop: '2rem' }}>{error}</p></div>;

  return (
    <div className="page-container">
      <h1>Admin Panel</h1>

      <div className="admin-tabs">
        <button
          className={tab === 'restaurants' ? 'tab active' : 'tab'}
          onClick={() => setTab('restaurants')}
        >
          Restaurants ({restaurants.length})
        </button>
        <button
          className={tab === 'menuItems' ? 'tab active' : 'tab'}
          onClick={() => setTab('menuItems')}
        >
          Menu Items ({menuItems.length})
        </button>
      </div>

      {tab === 'restaurants' && (
        <div className="pending-list">
          {restaurants.length === 0 ? (
            <p className="empty">No pending restaurants</p>
          ) : (
            restaurants.map((r) => (
              <div key={r.id} className="pending-card">
                <div className="pending-info">
                  <h3>{r.name}</h3>
                  <p>{r.address || 'No address'} | {r.phone || 'No phone'}</p>
                  <p className="pending-meta">Added by: {r.addedByUserName} | {new Date(r.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="pending-actions">
                  <button className="btn-approve" onClick={() => handleRestaurant(r.id, 'approve')}>Approve</button>
                  <button className="btn-reject" onClick={() => handleRestaurant(r.id, 'reject')}>Reject</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'menuItems' && (
        <div className="pending-list">
          {menuItems.length === 0 ? (
            <p className="empty">No pending menu items</p>
          ) : (
            menuItems.map((m) => (
              <div key={m.id} className="pending-card">
                <div className="pending-info">
                  <h3>{m.name} - {m.price.toFixed(2)}</h3>
                  <p>{m.restaurantName} | {m.category || 'No category'}</p>
                  <p className="pending-meta">Added by: {m.addedByUserName} | {new Date(m.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="pending-actions">
                  <button className="btn-approve" onClick={() => handleMenuItem(m.id, 'approve')}>Approve</button>
                  <button className="btn-reject" onClick={() => handleMenuItem(m.id, 'reject')}>Reject</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
